var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
import { jsx as _jsx, Fragment as _Fragment, jsxs as _jsxs } from "react/jsx-runtime";
import { getHighestUserRole } from 'lib-shared-user-role';
import { createContext, useEffect } from 'react';
import { usersApi } from 'apis/users';
import { useIsUserBannedOnChannel, useUserBans } from 'hooks/useBans';
import { pubSub } from 'utils/pubSub';
import { useAppPathParams } from 'utils/url/useAppPathParams';
import { contactLoadFailed, contactLoading, contactNotFound, noContactSelected, } from '../../utils/contactDetails.utils';
import { ContactsDetailsSection } from '../detailsSection/contactsDetailsSection';
import { UserAssignedConversations } from './components/userAssignedConversations/userAssignedConversations';
import { UserBanHistory } from './components/userBanHistory/userBanHistory';
import { UserConversations } from './components/userConversations/userConversations';
import { UserInfo } from './components/userInfo/userInfo';
// Looks like the Dynamo-Postgre sync lag is longer
// than in other places, but it's fine,
// because this is not the main table on the page
const fetchDelay = 2000;
export const UserBanOnChatContext = createContext({
    isLoadingBanStatus: false,
});
export function UserDetails() {
    var _a;
    const { id } = useAppPathParams('/contacts/user/:id');
    const [getUser, { user, isLoading, isError, isUninitialized }] = usersApi.useLazyReadUserQuery({
        selectFromResult: (_a) => {
            var { data } = _a, rest = __rest(_a, ["data"]);
            return (Object.assign({ user: data === null || data === void 0 ? void 0 : data.data }, rest));
        },
    });
    const { isBanned: isBannedOnChat, refreshStatus: isBannedOnChatRefreshStatus, asyncStatus: isBannedOnChatAsyncStatus, } = useIsUserBannedOnChannel(user === null || user === void 0 ? void 0 : user.id, 'widget_chat');
    const { items: bansHistoryItems, asyncStatus: bansHistoryAsyncStatus, hasNextPage: bansHistoryHasNextPage, hasPreviousPage: bansHistoryHasPreviousPage, nextPage: bansHistoryNextPage, previousPage: bansHistoryPreviousPage, refreshItems: bansHistoryRefreshItems, changeParams: bansHistoryChangeParams, } = useUserBans({
        end_user_id: id,
        limit: 6,
    });
    useEffect(() => {
        bansHistoryChangeParams({ end_user_id: id });
    }, [id, bansHistoryChangeParams]);
    useEffect(() => {
        const fetchUser = () => {
            if (id) {
                getUser({ id });
            }
        };
        fetchUser();
        const unsubscribe = pubSub.subscribe('userModalUserUpdated', fetchUser);
        return () => unsubscribe();
    }, [id, getUser]);
    if (!id) {
        return noContactSelected;
    }
    if (isLoading || isUninitialized) {
        return contactLoading;
    }
    if (isError) {
        return contactLoadFailed;
    }
    if (!user) {
        return contactNotFound;
    }
    const handleBanStatusToggle = () => __awaiter(this, void 0, void 0, function* () {
        isBannedOnChatRefreshStatus();
        setTimeout(() => {
            bansHistoryRefreshItems();
        }, fetchDelay);
    });
    const isStaff = !!((_a = user.roles) === null || _a === void 0 ? void 0 : _a.includes('agent'));
    return (_jsxs(_Fragment, { children: [_jsx(ContactsDetailsSection, { children: _jsx(UserBanOnChatContext.Provider, Object.assign({ value: {
                        isBanned: isBannedOnChat,
                        onBanStatusToggle: handleBanStatusToggle,
                        isLoadingBanStatus: isBannedOnChatAsyncStatus === 'loading',
                    } }, { children: _jsx(UserInfo, { user: user }) })) }), _jsx(ContactsDetailsSection, Object.assign({ headline: "Conversations" }, { children: _jsx(UserConversations, { user: user }) })), isStaff && (_jsx(ContactsDetailsSection, Object.assign({ headline: "Assigned conversations" }, { children: _jsx(UserAssignedConversations, { user: user }) }))), getHighestUserRole(user.roles) === 'enduser' && (_jsx(ContactsDetailsSection, Object.assign({ headline: "Ban history" }, { children: _jsx(UserBanHistory, { items: bansHistoryItems, asyncStatus: bansHistoryAsyncStatus, hasNextPage: bansHistoryHasNextPage, hasPreviousPage: bansHistoryHasPreviousPage, nextPage: bansHistoryNextPage, previousPage: bansHistoryPreviousPage }) })))] }));
}
